<template>
  <div class="mx-auto max-w-5xl space-y-6 p-6">
    <header class="space-y-2">
      <h1 class="text-3xl font-bold text-theme-fg">
        QR code generator
      </h1>
      <p class="text-theme-neutral-6">
        Turn any text or link into a QR code, in the browser. Nothing is uploaded.
      </p>
    </header>

    <Alert>
      <AlertTitle>Connecting two devices?</AlertTitle>
      <AlertDescription>
        The connection QR codes are made on
        <RouterLink to="/chat" class="font-medium text-theme-brand underline">Chat</RouterLink>
        and
        <RouterLink to="/file-transfer" class="font-medium text-theme-brand underline">File Transfer</RouterLink>:
        they contain a live WebRTC offer that only the page that created it can complete.
      </AlertDescription>
    </Alert>

    <div class="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
          <CardDescription>Text, a link, a Wi-Fi string — anything up to the QR capacity.</CardDescription>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="space-y-2">
            <label for="qr-content" class="text-sm font-medium text-theme-fg">Text or link</label>
            <Textarea id="qr-content" v-model="content" :rows="6" placeholder="https://example.org" />
            <p class="text-xs text-theme-neutral-6">
              {{ byteLength }} bytes (UTF-8) · capacity at this level: {{ capacity }} bytes
            </p>
          </div>

          <div class="space-y-2">
            <span id="qr-level-label" class="text-sm font-medium text-theme-fg">Error correction</span>
            <Select v-model="level">
              <SelectTrigger class="w-full" aria-labelledby="qr-level-label">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in LEVELS" :key="option.value" :value="option.value">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
            <p class="text-xs text-theme-neutral-6">
              Higher levels survive damage or a logo on the print, at the cost of a denser code.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>QR code</CardTitle>
          <CardDescription>
            <template v-if="linkKind === 'external'">
              Opens {{ linkHost }} when scanned.
            </template>
            <template v-else-if="linkKind === 'internal'">
              Opens a page of this app when scanned.
            </template>
            <template v-else-if="content.trim()">
              Plain text; a scanner shows it as text.
            </template>
            <template v-else>
              Enter some content first.
            </template>
          </CardDescription>
        </CardHeader>
        <CardContent class="flex flex-col items-center gap-4">
          <Alert v-if="tooLarge" variant="destructive">
            <AlertDescription>
              {{ byteLength }} bytes do not fit into one QR code at this level ({{ capacity }} max).
              Shorten the content or choose a lower error-correction level.
            </AlertDescription>
          </Alert>
          <Alert v-else-if="encodeError" variant="destructive">
            <AlertDescription>The QR code could not be generated: {{ encodeError }}</AlertDescription>
          </Alert>
          <QrCode
            v-else-if="content.trim()"
            :value="content"
            :level="level"
            :size="288"
            alt="Generated QR code"
            @encoded="svg = $event"
            @error="encodeError = $event.message"
          />
          <EmptyState v-else title="No content yet" description="The QR code appears here." />

          <div v-if="svg && !tooLarge && content.trim()" class="flex flex-wrap justify-center gap-2">
            <Button variant="outline" @click="downloadSvg">
              <Icon icon="lucide:download" class="h-4 w-4" aria-hidden="true" />
              Download SVG
            </Button>
            <ClipboardButton :text="content" label="Copy content" success-message="Content copied" />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Alert, AlertDescription, AlertTitle } from '@elveris/ui/alert'
import { Button } from '@elveris/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@elveris/ui/card'
import { ClipboardButton } from '@elveris/ui/clipboard-button'
import { EmptyState } from '@elveris/ui/empty-state'
import { Icon } from '@elveris/ui/icon'
import { QrCode } from '@elveris/ui/qr-code'
import { resolveQrLink } from '@elveris/ui/qr-scanner'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@elveris/ui/select'
import { Textarea } from '@elveris/ui/textarea'
import { computed, ref, watch } from 'vue'

// Rebuilt on @elveris/ui (2026-09-30). Archived previous version:
// _archive/kit-migration-2026-09-30/Generator.vue.pre-kit.
// The old page duplicated the host half of /chat: it created a WebRTC offer,
// but after the answer was applied there was nothing to do on the channel.
// Its "templates" put fixed strings such as "webrtc-video-call" into the
// offer's label and promised "HD video conference session" / "Digital
// contact card" that nothing implemented. The connection flows live on /chat
// and /file-transfer; this page keeps the function that stands on its own: a
// general QR code generator.

type Level = 'L' | 'M' | 'Q' | 'H'

const LEVELS: { value: Level, label: string }[] = [
  { value: 'L', label: 'L — ~7 % recovery (largest capacity)' },
  { value: 'M', label: 'M — ~15 % recovery' },
  { value: 'Q', label: 'Q — ~25 % recovery' },
  { value: 'H', label: 'H — ~30 % recovery' },
]

// Byte-mode capacity of a version-40 QR code (ISO/IEC 18004, table 7).
const MAX_BYTES: Record<Level, number> = { L: 2953, M: 2331, Q: 1663, H: 1273 }

const content = ref('')
const level = ref<Level>('M')
const svg = ref('')
const encodeError = ref('')

const byteLength = computed(() => new TextEncoder().encode(content.value).length)
const capacity = computed(() => MAX_BYTES[level.value])
const tooLarge = computed(() => byteLength.value > capacity.value)

// No window during SSG prerender: resolveQrLink needs an origin there.
const link = computed(() => (typeof window === 'undefined'
  ? { kind: 'text' as const, text: content.value }
  : resolveQrLink(content.value)))
const linkKind = computed(() => link.value.kind)
const linkHost = computed(() => (link.value.kind === 'external' ? link.value.url.host : ''))

watch([content, level], () => {
  svg.value = ''
  encodeError.value = ''
})

function downloadSvg() {
  const url = URL.createObjectURL(new Blob([svg.value], { type: 'image/svg+xml' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'qr-code.svg'
  a.click()
  URL.revokeObjectURL(url)
}
</script>
